import { useMutation } from '@tanstack/react-query'
import { BadgeCheck, KeyRound, MoreVertical, Pencil, UserCheck, UserPlus, UserX } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { api, errorText } from '../api.ts'
import { Button } from '../components/Button.tsx'
import { Dropdown, DropdownItem } from '../components/Dropdown.tsx'
import { SearchBox } from '../components/SearchBox.tsx'
import { SegmentedControl } from '../components/SegmentedControl.tsx'
import { SettingsGroup, SettingsRow } from '../components/SettingsGroup.tsx'
import { initials } from '../components/UserProfileDropdown.tsx'
import { useCompany, useCompanyCache } from '../company.ts'
import type { AppRef, Person } from '../types.ts'
import { AppMark, Badge, byKind, Field, itemName, Loading, Modal, Notice, when } from '../ui.tsx'
import { Copyable } from './McpServer.tsx'

type Dialog = { kind: 'add' } | { kind: 'edit' | 'status' | 'licenses' | 'password'; user: Person }
type Show = 'all' | 'invited' | 'disabled'

const lastSeen = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })

/** The company's users (people, in ICE's API), their licenses to each of the company's apps,
 *  and who's a company administrator (PLAN.md 1.6). New apps have no users screen of their
 *  own: this is it. */
export function Users({ companyId }: { companyId: string }) {
  const detail = useCompany(companyId)
  const cache = useCompanyCache(companyId)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [show, setShow] = useState<Show>('all')
  const [query, setQuery] = useState('')

  if (detail.isPending) return <Loading />
  if (detail.isError) return <Notice>{errorText(detail.error)}</Notice>
  const { people, apps, localSignin } = detail.data
  const base = `/companies/${companyId}/people`
  const close = () => setDialog(null)
  // A dialog shows the user as ICE last answered, not as they were when it opened.
  const current = dialog && dialog.kind !== 'add' ? people.find((p) => p.id === dialog.user.id) ?? null : null

  // Invited means someone active who hasn't signed in yet. The Invited filter is offered only
  // while there's someone invited; if the last one signs in, the list goes back to everyone.
  const isInvited = (p: Person) => p.invited && p.status === 'active'
  const anyInvited = people.some(isInvited)
  const filter: Show = show === 'invited' && !anyInvited ? 'all' : show
  const q = query.trim().toLowerCase()
  const shown = people
    .filter((p) => (filter === 'invited' ? isInvited(p) : filter === 'disabled' ? p.status === 'disabled' : true))
    .filter((p) => !q || p.displayName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }))

  return (
    <section>
      <div className="section-head">
        <h1>Users</h1>
        <Button icon={UserPlus} onClick={() => setDialog({ kind: 'add' })}>
          Add user
        </Button>
      </div>
      {people.length > 0 && (
        <div className="list-toolbar">
          <SegmentedControl
            label="Show"
            value={filter}
            onChange={setShow}
            options={[
              { value: 'all', label: 'All' },
              ...(anyInvited ? [{ value: 'invited' as const, label: 'Invited' }] : []),
              { value: 'disabled', label: 'Disabled' },
            ]}
          />
          <SearchBox value={query} onChange={setQuery} placeholder="Search users" width={280} />
        </div>
      )}
      {people.length === 0 ? (
        <p className="muted">Nobody yet.</p>
      ) : shown.length === 0 ? (
        <p className="muted">{q ? `Nobody matches “${query.trim()}”.` : filter === 'disabled' ? 'Nobody is disabled.' : 'Nobody here.'}</p>
      ) : (
        <ul className="list-rows card-list">
          {shown.map((p) => (
            <li key={p.id} className={p.status === 'disabled' ? 'dim' : undefined}>
              <span className="avatar" aria-hidden="true">{initials(p.displayName)}</span>
              <div className="user-text">
                <div className="user-name">
                  <strong>{p.displayName}</strong>
                  {/* A disabled user wears that tag alone. */}
                  {p.status === 'disabled' ? (
                    <Badge tone="warn">Disabled</Badge>
                  ) : (
                    <>
                      {p.iceRole === 'company_admin' && <Badge tone="accent">Admin</Badge>}
                      {p.signIn.password && <Badge>Password</Badge>}
                    </>
                  )}
                </div>
                <div className="muted small">{p.email}</div>
              </div>
              {/* Someone invited hasn't signed in yet: the tag says so in place of the date. */}
              <span className="user-last" title="Last sign-in">
                {isInvited(p) ? <Badge>Invited</Badge> : p.lastSignInAt ? lastSeen.format(new Date(p.lastSignInAt)) : 'Never signed in'}
              </span>
              <Dropdown
                align="end"
                trigger={({ open, toggle }) => (
                  <button type="button" className="icon-button icon-button-plain" onClick={toggle} aria-expanded={open} aria-haspopup="menu" aria-label={`Actions for ${p.displayName}`}>
                    <MoreVertical aria-hidden="true" />
                  </button>
                )}
              >
                <DropdownItem icon={Pencil} onClick={() => setDialog({ kind: 'edit', user: p })}>Edit</DropdownItem>
                <DropdownItem icon={p.status === 'active' ? UserX : UserCheck} onClick={() => setDialog({ kind: 'status', user: p })}>
                  {p.status === 'active' ? 'Disable' : 'Enable'}
                </DropdownItem>
                <DropdownItem icon={BadgeCheck} onClick={() => setDialog({ kind: 'licenses', user: p })}>Manage licenses</DropdownItem>
                {/* For someone with no Microsoft account: a guest, say. */}
                {localSignin && !p.signIn.external && p.status === 'active' && (
                  <DropdownItem icon={KeyRound} onClick={() => setDialog({ kind: 'password', user: p })}>
                    {p.signIn.password?.passwordSet ? 'Reset password' : 'Password sign-in'}
                  </DropdownItem>
                )}
              </Dropdown>
            </li>
          ))}
        </ul>
      )}

      <Modal title="Add user" open={dialog?.kind === 'add'} onClose={close}>
        <UserForm
          apps={apps}
          onSubmit={async (values) => {
            cache.putPerson(await api.post<Person>(base, values))
            close()
          }}
        />
      </Modal>
      <Modal title="Edit user" open={dialog?.kind === 'edit'} onClose={close}>
        {current && (
          <UserForm
            user={current}
            apps={apps}
            onSubmit={async (values) => {
              const { apps: _apps, ...rest } = values
              cache.putPerson(await api.patch<Person>(`${base}/${current.id}`, { ...rest, version: current.version }))
              close()
            }}
          />
        )}
      </Modal>
      <Modal title={current?.status === 'disabled' ? 'Enable user' : 'Disable user'} open={dialog?.kind === 'status'} onClose={close}>
        {current && <StatusForm user={current} base={base} onDone={close} />}
      </Modal>
      <Modal title="Manage licenses" open={dialog?.kind === 'licenses'} onClose={close}>
        {current && <Licenses user={current} apps={apps} base={base} onDone={close} />}
      </Modal>
      <Modal title="Password sign-in" open={dialog?.kind === 'password'} onClose={close}>
        {current && <PasswordLink user={current} base={base} onDone={close} />}
      </Modal>
    </section>
  )
}

/** Disabling or enabling a user, and deleting one, which only ever starts from here. */
function StatusForm({ user, base, onDone }: { user: Person; base: string; onDone: () => void }) {
  const cache = useCompanyCache(user.companyId)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const act = useMutation({
    mutationFn: (run: () => Promise<Person>) => run(),
    onMutate: () => setError(null),
    onSuccess: (person) => {
      cache.putPerson(person)
      onDone()
    },
    onError: (err) => setError(errorText(err)),
  })
  const disabled = user.status === 'disabled'
  const setStatus = () =>
    act.mutate(() => api.patch<Person>(`${base}/${user.id}`, { version: user.version, status: disabled ? 'active' : 'disabled' }))
  const remove = () => act.mutate(() => api.delete<Person>(`${base}/${user.id}?version=${user.version}`))

  return (
    <div className="form">
      <Notice>{error}</Notice>
      {disabled ? (
        <p className="muted">{user.displayName} can't sign in to any app. Enabling them gives back the licenses they had.</p>
      ) : (
        <p className="muted">{user.displayName} won't be able to sign in to any app. Their licenses and history are kept, so you can enable them again later.</p>
      )}
      <div className="form-actions">
        <Button variant="secondary" onClick={onDone}>Cancel</Button>
        <Button disabled={act.isPending} onClick={setStatus}>{disabled ? 'Enable' : 'Disable'}</Button>
      </div>
      <div className="danger-zone">
        <h3>Delete user</h3>
        {confirmDelete ? (
          <>
            <p className="muted small">Delete {user.displayName} for good? They won't be able to sign in, and they can't be brought back. Their history is kept.</p>
            <div className="form-actions">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>Keep</Button>
              <Button variant="destructive" disabled={act.isPending} onClick={remove}>Delete {user.displayName}</Button>
            </div>
          </>
        ) : (
          <div className="alert-row">
            <p className="muted small">Removes them from the company. Their history is kept.</p>
            <Button variant="destructive-tertiary" onClick={() => setConfirmDelete(true)}>Delete…</Button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A set-password link, for someone with no Microsoft account. ICE sends no email: the
 * administrator passes the link on. For someone who has a password it's a reset, which stops
 * the old one at once and signs them out everywhere.
 */
function PasswordLink({ user, base, onDone }: { user: Person; base: string; onDone: () => void }) {
  const cache = useCompanyCache(user.companyId)
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null)
  const issue = useMutation({
    mutationFn: () => api.post<{ url: string; expiresAt: string; person: Person }>(`${base}/${user.id}/password-link`),
    onSuccess: ({ person, ...made }) => {
      cache.putPerson(person)
      setLink(made)
    },
  })
  const password = user.signIn.password
  const reset = password?.passwordSet ?? false

  if (link) {
    return (
      <div className="form">
        <p className="muted">
          Send this link to {user.displayName} yourself, by a way only they will see. It works once, until {when(link.expiresAt)}, and
          it won't be shown again.
        </p>
        <Copyable label="Set-password link" value={link.url} />
        <div className="form-actions">
          <Button onClick={onDone}>Done</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="form">
      <Notice>{issue.isError ? errorText(issue.error) : null}</Notice>
      {reset ? (
        <p className="muted">
          {user.displayName} signs in with a password, set {when(password!.setAt)}. A new link lets them choose another. Their
          current password stops working now, and they're signed out everywhere.
        </p>
      ) : (
        <p className="muted">
          For someone without a Microsoft account. They'll sign in as {user.email} with a password they choose from a link
          you send them.
          {password?.linkExpiresAt && ` The link you made before works until ${when(password.linkExpiresAt)}. A new one replaces it.`}
        </p>
      )}
      {password?.lockedUntil && <Notice kind="info">Locked after too many wrong passwords, until {when(password.lockedUntil)}.</Notice>}
      <div className="form-actions">
        <Button variant="secondary" onClick={onDone}>Cancel</Button>
        <Button disabled={issue.isPending} onClick={() => issue.mutate()}>{reset ? 'Reset password' : 'Make a link'}</Button>
      </div>
    </div>
  )
}

/** Which of the company's apps and MCP servers the user may use. The switches only change
 *  what's shown; Done saves the difference, and the X discards it. */
function Licenses({ user, apps, base, onDone }: { user: Person; apps: AppRef[]; base: string; onDone: () => void }) {
  const cache = useCompanyCache(user.companyId)
  const [error, setError] = useState<string | null>(null)
  const [chosen, setChosen] = useState(() => new Set(user.apps))
  const save = useMutation({
    mutationFn: async () => {
      for (const a of apps) {
        const enabled = chosen.has(a.appId)
        if (user.apps.includes(a.appId) === enabled) continue
        cache.putPerson(await api.put<Person>(`${base}/${user.id}/apps/${a.appId}`, { enabled }))
      }
    },
    onMutate: () => setError(null),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  })

  return (
    <div className="form">
      <p className="muted">Which apps and MCP servers {user.displayName} can use. Only those your company has are listed. Changes are saved when you press Done.</p>
      <Notice>{error}</Notice>
      {apps.length === 0 ? (
        <p className="muted">Your company has no apps yet.</p>
      ) : (
        byKind(apps).map((group) => (
          <div key={group.kind}>
            <h3 className="kind-label">{group.label}</h3>
            <ul className="list-rows">
              {group.items.map((a) => (
                <li key={a.appId}>
                  <AppMark appId={a.appId} name={a.name} kind={a.kind} size={32} />
                  <label className="list-text check" htmlFor={`license-${a.appId}`}>{itemName(a)}</label>
                  <input
                    id={`license-${a.appId}`}
                    type="checkbox"
                    role="switch"
                    className="switch"
                    checked={chosen.has(a.appId)}
                    disabled={save.isPending}
                    onChange={(e) =>
                      setChosen((prev) => {
                        const next = new Set(prev)
                        if (e.target.checked) next.add(a.appId)
                        else next.delete(a.appId)
                        return next
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
      <div className="form-actions">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>Done</Button>
      </div>
    </div>
  )
}

interface UserValues {
  displayName: string
  email: string
  phone: string | null
  companyAdmin: boolean
  apps: string[]
}

function UserForm({
  user,
  apps,
  onSubmit,
}: {
  user?: Person
  apps: AppRef[]
  onSubmit: (values: UserValues) => Promise<void>
}) {
  const [values, setValues] = useState<UserValues>({
    displayName: user?.displayName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    companyAdmin: user?.iceRole === 'company_admin',
    // Someone new gets a license to every app the company has, unless unticked.
    apps: user?.apps ?? apps.map((a) => a.appId),
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onSubmit({ ...values, phone: values.phone?.trim() ? values.phone : null })
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice>{error}</Notice>
      <Field label="Name">
        <input required value={values.displayName} onChange={(e) => setValues({ ...values, displayName: e.target.value })} />
      </Field>
      <Field label="Email" hint="They sign in with the Microsoft account that has this address.">
        <input type="email" required value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} />
      </Field>
      <Field label="Phone">
        <input type="tel" value={values.phone ?? ''} onChange={(e) => setValues({ ...values, phone: e.target.value })} />
      </Field>
      {!user && byKind(apps).map((group) => (
        <SettingsGroup key={group.kind} label={group.label}>
          {group.items.map((a) => (
            <SettingsRow
              key={a.appId}
              leading={<AppMark appId={a.appId} name={a.name} kind={a.kind} size={28} />}
              title={itemName(a)}
              labelFor={`new-license-${a.appId}`}
              trailing={
                <input
                  id={`new-license-${a.appId}`}
                  type="checkbox"
                  role="switch"
                  className="switch"
                  checked={values.apps.includes(a.appId)}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      apps: e.target.checked ? [...values.apps, a.appId] : values.apps.filter((x) => x !== a.appId),
                    })
                  }
                />
              }
            />
          ))}
        </SettingsGroup>
      ))}
      <SettingsGroup label="Role">
        <SettingsRow
          title="Company administrator"
          subtitle="Can manage users, licenses and company details."
          labelFor="company-admin"
          trailing={
            <input
              id="company-admin"
              type="checkbox"
              role="switch"
              className="switch"
              checked={values.companyAdmin}
              onChange={(e) => setValues({ ...values, companyAdmin: e.target.checked })}
            />
          }
        />
      </SettingsGroup>
      <div className="form-actions">
        <Button type="submit" disabled={busy}>
          {user ? 'Save' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
